import React, { useState, useEffect } from 'react';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { useNavigate } from 'react-router-dom';
dayjs.extend(utc);
dayjs.extend(timezone);
import {
  EyeIcon,
  CalendarIcon,
  ClockIcon,
  CheckCircleIcon,
  XCircleIcon,
  FunnelIcon,
  MagnifyingGlassIcon,
  ClipboardDocumentListIcon,
  ArrowTrendingUpIcon
} from '@heroicons/react/24/outline';
import config from '../config/api';

const Projects = () => {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedProject, setSelectedProject] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [filterStatus, setFilterStatus] = useState('ALL');
  const [searchQuery, setSearchQuery] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    fetchProjects();
  }, []);

  const fetchProjects = async () => {
    try {
      const token = localStorage.getItem('token');
      const customerId = localStorage.getItem('customer_id');
      let url = config.PROJECTS_URL;
      if (customerId) {
        url += `?customer_id=${customerId}`;
      }
      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setProjects(data);
      }
    } catch (error) {
      console.error('Error fetching projects:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      'PENDING': 'status-pending',
      'ACTIVE': 'status-active',
      'COMPLETED': 'status-completed',
      'CANCELLED': 'status-expired',
      'ON_HOLD': 'bg-slate-100 text-slate-700'
    };
    return colors[status] || 'bg-slate-100 text-slate-700';
  };

  const getStatusText = (status) => {
    const statusMap = {
      'ACTIVE': 'Đang thực hiện',
      'COMPLETED': 'Hoàn thành',
      'PENDING': 'Chờ xử lý',
      'CANCELLED': 'Đã hủy',
      'ON_HOLD': 'Tạm dừng'
    };
    return statusMap[status] || status;
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'ACTIVE':
        return <ClockIcon className="w-4 h-4" />;
      case 'COMPLETED':
        return <CheckCircleIcon className="w-4 h-4" />;
      case 'CANCELLED':
        return <XCircleIcon className="w-4 h-4" />;
      default:
        return <CalendarIcon className="w-4 h-4" />;
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    return dayjs.utc(dateString).tz('Asia/Ho_Chi_Minh').format('DD/MM/YYYY');
  };

  const getProgressPercentage = (project) => {
    if (project.status === 'COMPLETED') return 100;
    if (project.status === 'CANCELLED') return 0;
    if (!project.start_date || !project.end_date) return 0;

    const startDate = new Date(project.start_date);
    const endDate = new Date(project.end_date);
    const currentDate = new Date();

    if (currentDate < startDate) return 0;
    if (currentDate > endDate) return 100;

    const totalDuration = endDate.getTime() - startDate.getTime();
    const elapsedDuration = currentDate.getTime() - startDate.getTime();

    return Math.round((elapsedDuration / totalDuration) * 100);
  };

  const getProgressColor = (percentage) => {
    if (percentage >= 80) return 'from-emerald-400 to-emerald-600';
    if (percentage >= 50) return 'from-blue-400 to-blue-600';
    if (percentage >= 25) return 'from-amber-400 to-amber-600';
    return 'from-slate-300 to-slate-400';
  };

  const filteredProjects = projects.filter(project => {
    const matchesStatus = filterStatus === 'ALL' || project.status === filterStatus;
    const matchesSearch = project.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         (project.description && project.description.toLowerCase().includes(searchQuery.toLowerCase()));
    return matchesStatus && matchesSearch;
  });

  // Calculate stats
  const stats = {
    total: projects.length,
    active: projects.filter(p => p.status === 'ACTIVE').length,
    completed: projects.filter(p => p.status === 'COMPLETED').length,
    pending: projects.filter(p => p.status === 'PENDING').length
  };

  const handleViewDetails = (project) => {
    setSelectedProject(project);
    setShowModal(true);
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-96">
        <div className="relative">
          <div className="w-20 h-20 border-4 border-primary-200 rounded-full"></div>
          <div className="w-20 h-20 border-4 border-primary-500 rounded-full animate-spin absolute top-0 left-0 border-t-transparent"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Stats Overview */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="glass-card rounded-2xl p-5 hover-lift">
          <div className="flex items-center gap-3">
            <div className="p-2.5 bg-blue-100 rounded-xl">
              <ClipboardDocumentListIcon className="w-6 h-6 text-blue-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-slate-800">{stats.total}</p>
              <p className="text-sm text-slate-500">Tổng dự án</p>
            </div>
          </div>
        </div>
        <div className="glass-card rounded-2xl p-5 hover-lift">
          <div className="flex items-center gap-3">
            <div className="p-2.5 bg-emerald-100 rounded-xl">
              <ArrowTrendingUpIcon className="w-6 h-6 text-emerald-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-slate-800">{stats.active}</p>
              <p className="text-sm text-slate-500">Đang chạy</p>
            </div>
          </div>
        </div>
        <div className="glass-card rounded-2xl p-5 hover-lift">
          <div className="flex items-center gap-3">
            <div className="p-2.5 bg-purple-100 rounded-xl">
              <CheckCircleIcon className="w-6 h-6 text-purple-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-slate-800">{stats.completed}</p>
              <p className="text-sm text-slate-500">Hoàn thành</p>
            </div>
          </div>
        </div>
        <div className="glass-card rounded-2xl p-5 hover-lift">
          <div className="flex items-center gap-3">
            <div className="p-2.5 bg-amber-100 rounded-xl">
              <ClockIcon className="w-6 h-6 text-amber-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-slate-800">{stats.pending}</p>
              <p className="text-sm text-slate-500">Chờ xử lý</p>
            </div>
          </div>
        </div>
      </div>

      {/* Filter & Search */}
      <div className="glass-card rounded-2xl p-5">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1 relative">
            <MagnifyingGlassIcon className="w-5 h-5 text-slate-400 absolute left-4 top-1/2 -translate-y-1/2" />
            <input
              type="text"
              placeholder="Tìm kiếm dự án..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="form-input-modern pl-12"
            />
          </div>
          <div className="flex items-center gap-2">
            <FunnelIcon className="w-5 h-5 text-slate-400" />
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="form-input-modern w-auto"
            >
              <option value="ALL">Tất cả trạng thái</option>
              <option value="PENDING">Chờ xử lý</option>
              <option value="ACTIVE">Đang thực hiện</option>
              <option value="COMPLETED">Hoàn thành</option>
              <option value="ON_HOLD">Tạm dừng</option>
              <option value="CANCELLED">Đã hủy</option>
            </select>
          </div>
        </div>
      </div>

      {/* Projects Grid */}
      {filteredProjects.length === 0 ? (
        <div className="glass-card rounded-2xl p-12 text-center">
          <ClipboardDocumentListIcon className="w-16 h-16 text-slate-300 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-slate-800 mb-2">Không tìm thấy dự án</h3>
          <p className="text-slate-500">
            {filterStatus === 'ALL'
              ? "Bạn chưa có dự án nào."
              : `Không có dự án nào với trạng thái "${getStatusText(filterStatus)}".`
            }
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredProjects.map((project, index) => (
            <div
              key={project.id}
              className="card-interactive rounded-2xl overflow-hidden"
              style={{ animationDelay: `${index * 0.05}s` }}
            >
              {/* Card Header with gradient */}
              <div className={`h-2 bg-gradient-to-r ${getProgressColor(getProgressPercentage(project))}`}></div>

              <div className="p-6">
                <div className="flex items-start justify-between mb-4">
                  <h3 className="text-lg font-bold text-slate-800 line-clamp-2 flex-1 mr-3">
                    {project.name}
                  </h3>
                  <span className={`status-badge ${getStatusColor(project.status)} flex-shrink-0`}>
                    {getStatusIcon(project.status)}
                    <span className="ml-1">{getStatusText(project.status)}</span>
                  </span>
                </div>

                <p className="text-slate-500 text-sm mb-5 line-clamp-2">
                  {project.description || 'Không có mô tả'}
                </p>

                {/* Progress Bar */}
                <div className="mb-5">
                  <div className="flex justify-between text-sm mb-2">
                    <span className="text-slate-500 font-medium">Tiến độ</span>
                    <span className="font-bold text-slate-700">{getProgressPercentage(project)}%</span>
                  </div>
                  <div className="progress-bar">
                    <div
                      className={`progress-bar-fill bg-gradient-to-r ${getProgressColor(getProgressPercentage(project))}`}
                      style={{ width: `${getProgressPercentage(project)}%` }}
                    ></div>
                  </div>
                </div>

                {/* Project Details */}
                <div className="space-y-2 text-sm text-slate-500 mb-5">
                  <div className="flex items-center gap-2">
                    <CalendarIcon className="w-4 h-4 text-slate-400" />
                    <span>Bắt đầu: {formatDate(project.start_date)}</span>
                  </div>
                  {project.end_date && (
                    <div className="flex items-center gap-2">
                      <CalendarIcon className="w-4 h-4 text-slate-400" />
                      <span>Kết thúc: {formatDate(project.end_date)}</span>
                    </div>
                  )}
                </div>

                {/* Actions */}
                <button
                  onClick={() => handleViewDetails(project)}
                  className="w-full btn-gradient-primary flex items-center justify-center gap-2"
                >
                  <EyeIcon className="w-5 h-5" />
                  Xem chi tiết
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Project Details Modal */}
      {showModal && selectedProject && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-3xl max-w-2xl w-full max-h-[90vh] overflow-y-auto shadow-2xl animate-fade-in">
            {/* Modal Header */}
            <div className={`h-3 bg-gradient-to-r ${getProgressColor(getProgressPercentage(selectedProject))}`}></div>

            <div className="p-8">
              <div className="flex justify-between items-start mb-6">
                <div>
                  <h2 className="text-2xl font-bold text-slate-800 mb-2">{selectedProject.name}</h2>
                  <span className={`status-badge ${getStatusColor(selectedProject.status)}`}>
                    {getStatusIcon(selectedProject.status)}
                    <span className="ml-1">{getStatusText(selectedProject.status)}</span>
                  </span>
                </div>
                <button
                  onClick={() => setShowModal(false)}
                  className="p-2 hover:bg-slate-100 rounded-xl transition-colors"
                >
                  <XCircleIcon className="w-6 h-6 text-slate-400" />
                </button>
              </div>

              <div className="space-y-6">
                {/* Description */}
                <div>
                  <h4 className="font-semibold text-slate-700 mb-2">Mô tả</h4>
                  <p className="text-slate-600 bg-slate-50 p-4 rounded-xl">{selectedProject.description || 'Không có mô tả'}</p>
                </div>

                {/* Progress */}
                <div>
                  <h4 className="font-semibold text-slate-700 mb-3">Tiến độ tổng thể</h4>
                  <div className="flex justify-between text-sm mb-2">
                    <span className="text-slate-500">Hoàn thành</span>
                    <span className="font-bold text-slate-700">{getProgressPercentage(selectedProject)}%</span>
                  </div>
                  <div className="h-4 bg-slate-200 rounded-full overflow-hidden">
                    <div
                      className={`h-full rounded-full bg-gradient-to-r ${getProgressColor(getProgressPercentage(selectedProject))} transition-all duration-500`}
                      style={{ width: `${getProgressPercentage(selectedProject)}%` }}
                    ></div>
                  </div>
                </div>

                {/* Timeline */}
                <div>
                  <h4 className="font-semibold text-slate-700 mb-3">Thời gian thực hiện</h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="bg-blue-50 p-4 rounded-xl">
                      <div className="text-sm text-blue-600 font-medium mb-1">Ngày bắt đầu</div>
                      <div className="font-bold text-slate-800">{formatDate(selectedProject.start_date)}</div>
                    </div>
                    {selectedProject.end_date && (
                      <div className="bg-emerald-50 p-4 rounded-xl">
                        <div className="text-sm text-emerald-600 font-medium mb-1">Ngày kết thúc</div>
                        <div className="font-bold text-slate-800">{formatDate(selectedProject.end_date)}</div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Budget */}
                {selectedProject.budget && (
                  <div>
                    <h4 className="font-semibold text-slate-700 mb-2">Ngân sách</h4>
                    <div className="bg-gradient-to-r from-emerald-500 to-teal-500 p-5 rounded-xl text-white">
                      <div className="text-3xl font-bold">
                        {new Intl.NumberFormat('vi-VN').format(selectedProject.budget)} VNĐ
                      </div>
                    </div>
                  </div>
                )}

                {/* Notes */}
                {selectedProject.notes && (
                  <div>
                    <h4 className="font-semibold text-slate-700 mb-2">Ghi chú</h4>
                    <div className="bg-amber-50 p-4 rounded-xl border border-amber-200">
                      <p className="text-slate-700 whitespace-pre-wrap">{selectedProject.notes}</p>
                    </div>
                  </div>
                )}
              </div>

              <div className="mt-8 flex gap-3">
                <button
                  onClick={() => navigate(`/projects/${selectedProject.id}/tasks`)}
                  className="flex-1 btn-gradient-primary"
                >
                  Xem công việc
                </button>
                <button
                  onClick={() => setShowModal(false)}
                  className="px-6 py-2.5 bg-slate-100 text-slate-700 rounded-xl hover:bg-slate-200 transition-colors font-semibold"
                >
                  Đóng
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Projects;
